// Simple browser-persistent rate limiter against brute-force attempts
interface RateLimitRecord {
  attempts: number;
  lastAttempt: number;
  blockedUntil: number;
}

export function checkRateLimit(key: string, maxAttempts = 5, windowMs = 60000, blockDurationMs = 60000): { allowed: boolean; remainingSeconds: number; attemptsLeft: number } {
  const storageKey = `rate_limit_${key}`;
  const now = Date.now();

  let record: RateLimitRecord = { attempts: 0, lastAttempt: now, blockedUntil: 0 };
  
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      record = JSON.parse(raw);
    }
  } catch {
    // fallback
  }

  // Check if currently blocked
  if (record.blockedUntil > now) {
    const remainingSeconds = Math.ceil((record.blockedUntil - now) / 1000);
    return { allowed: false, remainingSeconds, attemptsLeft: 0 };
  }

  // Reset window if last attempt was outside windowMs
  if (now - record.lastAttempt > windowMs) {
    record.attempts = 0;
    record.blockedUntil = 0;
  }

  const attemptsLeft = Math.max(0, maxAttempts - record.attempts);
  return { allowed: true, remainingSeconds: 0, attemptsLeft };
}

export function recordFailedAttempt(key: string, maxAttempts = 5, windowMs = 60000, blockDurationMs = 60000): { blocked: boolean; remainingSeconds: number } {
  const storageKey = `rate_limit_${key}`;
  const now = Date.now();

  let record: RateLimitRecord = { attempts: 0, lastAttempt: now, blockedUntil: 0 };
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) record = JSON.parse(raw);
  } catch {}

  if (now - record.lastAttempt > windowMs) {
    record.attempts = 0;
  }

  record.attempts += 1;
  record.lastAttempt = now;

  if (record.attempts >= maxAttempts) {
    record.blockedUntil = now + blockDurationMs;
    try {
      localStorage.setItem(storageKey, JSON.stringify(record));
    } catch {}
    return { blocked: true, remainingSeconds: Math.ceil(blockDurationMs / 1000) };
  }

  try {
    localStorage.setItem(storageKey, JSON.stringify(record));
  } catch {}

  return { blocked: false, remainingSeconds: 0 };
}

export function resetRateLimit(key: string): void {
  try {
    localStorage.removeItem(`rate_limit_${key}`);
  } catch {}
}
